/**
 * Delivery Estimator - Phase 4.2
 * Calculates and displays dispatch/delivery dates in real-time.
 * Cut-off time countdown, business-day aware, weekend and bank-holiday aware.
 *
 * The cut-off, despatch calendar, holiday list and per-service transit windows
 * are NOT defined here — they come from window.KTDeliveryPromise, generated from
 * config/delivery-promise.json by scripts/generate-delivery-promise.js. This file
 * previously carried a hardcoded BANK_HOLIDAYS_2026 array that would have expired
 * silently on 2027-01-01 (every 2027 bank holiday becoming a working day, with no
 * error), and a 14:00 cut-off default that disagreed with the 12pm the store
 * actually ships.
 *
 * The date arithmetic is exported as window.KTDelivery so other surfaces can ask
 * the same questions, and so the weekend/holiday branches are unit-testable —
 * before this they were sealed inside the IIFE and no test exercised them.
 */
(function () {
  'use strict';

  var DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  function isoOf(date) {
    return date.getFullYear() + '-' +
      String(date.getMonth() + 1).padStart(2, '0') + '-' +
      String(date.getDate()).padStart(2, '0');
  }

  function formatDate(date) {
    return DAY_NAMES[date.getDay()] + ' ' +
      date.getDate() + ' ' +
      MONTH_NAMES[date.getMonth()];
  }

  function formatDateRange(minDate, maxDate) {
    if (minDate.getTime() === maxDate.getTime()) {
      return formatDate(minDate);
    }
    if (minDate.getMonth() === maxDate.getMonth()) {
      return DAY_NAMES[minDate.getDay()] + ' ' + minDate.getDate() +
        ' - ' + DAY_NAMES[maxDate.getDay()] + ' ' + maxDate.getDate() +
        ' ' + MONTH_NAMES[minDate.getMonth()];
    }
    return formatDate(minDate) + ' - ' + formatDate(maxDate);
  }

  function getUKTime() {
    // Get current time in UK timezone
    var now = new Date();
    var ukString = now.toLocaleString('en-GB', { timeZone: 'Europe/London' });
    var parts = ukString.split(/[\/,\s:]+/);
    return new Date(
      parseInt(parts[2]),
      parseInt(parts[1]) - 1,
      parseInt(parts[0]),
      parseInt(parts[3]),
      parseInt(parts[4]),
      parseInt(parts[5])
    );
  }

  /**
   * Builds the date API for one promise document. A factory rather than a
   * singleton so tests can exercise several calendars (and several "today"s)
   * without reloading the file.
   */
  function createDelivery(promise) {
    if (!promise || !promise.cutoff || !promise.despatchDays) {
      throw new Error('KTDelivery: window.KTDeliveryPromise is missing or malformed');
    }

    var holidays = {};
    (promise.bankHolidays || []).forEach(function (iso) { holidays[iso] = true; });

    function isBankHoliday(date) {
      return holidays[isoOf(date)] === true;
    }

    function isWeekend(date) {
      var day = date.getDay();
      return day === 0 || day === 6;
    }

    // The carrier's working calendar — what a "business day" of transit means.
    function isBusinessDay(date) {
      return !isWeekend(date) && !isBankHoliday(date);
    }

    // KT's own calendar — the days we actually put parcels on a van. Driven by
    // config so a change of hours is a config edit, not a code edit.
    function isDespatchDay(date) {
      return promise.despatchDays.indexOf(date.getDay()) !== -1 && !isBankHoliday(date);
    }

    function addBusinessDays(startDate, numDays) {
      var date = new Date(startDate);
      var added = 0;
      while (added < numDays) {
        date.setDate(date.getDate() + 1);
        if (isBusinessDay(date)) {
          added++;
        }
      }
      return date;
    }

    function nextBusinessDay(date) {
      var d = new Date(date);
      d.setDate(d.getDate() + 1);
      while (!isBusinessDay(d)) {
        d.setDate(d.getDate() + 1);
      }
      return d;
    }

    function nextDespatchDay(date) {
      var d = new Date(date);
      d.setDate(d.getDate() + 1);
      while (!isDespatchDay(d)) {
        d.setDate(d.getDate() + 1);
      }
      return d;
    }

    function beforeCutoff(now) {
      var h = now.getHours();
      var m = now.getMinutes();
      return h < promise.cutoff.hour ||
        (h === promise.cutoff.hour && m < promise.cutoff.minute);
    }

    /**
     * The day this order actually leaves. Today only if we still despatch today
     * AND the cut-off has not passed; otherwise the next day we despatch at all.
     */
    function nextDespatchDate(now) {
      if (isDespatchDay(now) && beforeCutoff(now)) return new Date(now);
      return nextDespatchDay(now);
    }

    /**
     * Whether an expedited rate may be sold right now. This is the rule the
     * AIMarketOps cron enforces on the Shopify rates themselves: KT was selling
     * "Next working day" at £9.99 all weekend against a Mon-Fri despatch, so
     * Saturday shoppers paid for a service that could not exist.
     *
     * Friday before the cut-off stays available on purpose — Saturday is not a
     * working day, so "next working day" from Friday is Monday, and arrivalWindow
     * returns exactly that date for the storefront to state.
     */
    function expeditedAvailable(now) {
      var rules = promise.expedited || {};
      var days = rules.sellableDespatchDays || promise.despatchDays;
      if (days.indexOf(now.getDay()) === -1) return false;
      if (isBankHoliday(now)) return false;
      if (rules.requiresBeforeCutoff && !beforeCutoff(now)) return false;
      return true;
    }

    /**
     * Arrival window for a named service, counted in carrier business days from
     * the despatch day — so a Friday despatch on a one-day service lands Monday,
     * never Saturday.
     */
    function arrivalWindow(now, serviceKey) {
      var service = (promise.services || {})[serviceKey];
      if (!service) throw new Error('KTDelivery: unknown service "' + serviceKey + '"');
      var despatch = nextDespatchDate(now);
      var transit = service.transitBusinessDays;
      return {
        despatch: despatch,
        min: addBusinessDays(despatch, transit.minimum),
        max: addBusinessDays(despatch, transit.maximum)
      };
    }

    return {
      promise: promise,
      isoOf: isoOf,
      isWeekend: isWeekend,
      isBankHoliday: isBankHoliday,
      isBusinessDay: isBusinessDay,
      isDespatchDay: isDespatchDay,
      addBusinessDays: addBusinessDays,
      nextBusinessDay: nextBusinessDay,
      nextDespatchDay: nextDespatchDay,
      beforeCutoff: beforeCutoff,
      nextDespatchDate: nextDespatchDate,
      expeditedAvailable: expeditedAvailable,
      arrivalWindow: arrivalWindow,
      formatDate: formatDate,
      formatDateRange: formatDateRange,
      getUKTime: getUKTime
    };
  }

  function initEstimator(el, api) {
    var cutoff = api.promise.cutoff;
    var deliveryMin = parseInt(el.dataset.deliveryMin) ||
      api.promise.services.standard.transitBusinessDays.minimum;
    var deliveryMax = parseInt(el.dataset.deliveryMax) ||
      api.promise.services.standard.transitBusinessDays.maximum;
    var isNextDay = el.dataset.nextDay === 'true';

    var dispatchDateEl = el.querySelector('[data-estimator-dispatch-date]');
    var deliveryDateEl = el.querySelector('[data-estimator-delivery-date]');
    var countdownEl = el.querySelector('[data-estimator-countdown]');
    var countdownTextEl = el.querySelector('[data-estimator-countdown-text]');

    function update() {
      var now = api.getUKTime();
      var canDespatchToday = api.isDespatchDay(now) && api.beforeCutoff(now);

      // Determine dispatch date. Bank holidays and weekends are handled inside
      // isDespatchDay/nextDespatchDay, so there is no branch here that can skip
      // the holiday check — the previous weekend-dispatch path added a bare 24h
      // and stepped straight onto Christmas Day.
      var dispatchDate = api.nextDespatchDate(now);
      if (canDespatchToday) {
        el.classList.add('kt-delivery-estimator--today');
      } else {
        el.classList.remove('kt-delivery-estimator--today');
      }

      // Display dispatch date
      var isToday = dispatchDate.toDateString() === now.toDateString();
      var tomorrow = new Date(now);
      tomorrow.setDate(tomorrow.getDate() + 1);
      var isTomorrow = dispatchDate.toDateString() === tomorrow.toDateString();

      if (isToday) {
        dispatchDateEl.textContent = 'Today';
      } else if (isTomorrow) {
        dispatchDateEl.textContent = 'Tomorrow (' + api.formatDate(dispatchDate) + ')';
      } else {
        dispatchDateEl.textContent = api.formatDate(dispatchDate);
      }

      // Countdown timer (only while today's despatch is still reachable)
      if (countdownEl && countdownTextEl) {
        if (canDespatchToday) {
          var cutoffTime = new Date(now);
          cutoffTime.setHours(cutoff.hour, cutoff.minute, 0, 0);
          var diffMs = cutoffTime - now;
          var diffHrs = Math.floor(diffMs / 3600000);
          var diffMins = Math.floor((diffMs % 3600000) / 60000);

          var countdownText = 'Order within ';
          if (diffHrs > 0) {
            countdownText += diffHrs + 'h ' + diffMins + 'min';
          } else {
            countdownText += diffMins + ' min';
          }
          countdownText += ' for dispatch today';

          countdownTextEl.textContent = countdownText;
          countdownEl.style.display = '';
        } else {
          countdownEl.style.display = 'none';
        }
      }

      // Delivery date estimate. A next-day product is only genuinely next-day
      // while an expedited rate is actually purchasable; outside that window it
      // falls back to the standard transit range rather than promising a day
      // that checkout will not sell.
      var nextDayNow = isNextDay && api.expeditedAvailable(now);
      var actualDeliveryMin = nextDayNow ? 1 : deliveryMin;
      var actualDeliveryMax = nextDayNow ? 1 : deliveryMax;

      var deliveryMinDate = api.addBusinessDays(dispatchDate, actualDeliveryMin);
      var deliveryMaxDate = api.addBusinessDays(dispatchDate, actualDeliveryMax);

      // The estimated-delivery row is optional (hidden by default since
      // 2026-07-23 — carriage varies by brand/weight); dispatch + countdown
      // must keep working when it is not rendered.
      if (deliveryDateEl) {
        if (nextDayNow) {
          el.classList.add('kt-delivery-estimator--next-day');
          deliveryDateEl.textContent = api.formatDate(deliveryMinDate) + ' (Next Day)';
        } else {
          el.classList.remove('kt-delivery-estimator--next-day');
          deliveryDateEl.textContent = api.formatDateRange(deliveryMinDate, deliveryMaxDate);
        }
      }
    }

    // KT-206: the Liquid renders a real promise ("Next working day"), not an
    // empty placeholder. If this computation ever throws, that server-rendered
    // text must survive rather than being replaced by a broken or blank value,
    // so every run is guarded and the fallback marker is only cleared once an
    // update has actually completed. A missing window.KTDeliveryPromise reaches
    // here as a throw, which is the intended failure: no promise, no computed
    // date, and the honest server-rendered one stays.
    function safeUpdate() {
      try {
        update();
        [dispatchDateEl, deliveryDateEl].forEach(function (node) {
          if (node) node.removeAttribute('data-estimator-fallback');
        });
      } catch (error) {
        // Leave the server-rendered default in place. Stop refreshing so a
        // repeating exception cannot flood the console.
        if (typeof intervalId !== 'undefined') clearInterval(intervalId);
      }
    }

    // Initial update
    safeUpdate();

    // Refresh every 60 seconds for countdown accuracy
    var intervalId = setInterval(safeUpdate, 60000);

    // Cleanup on page navigation (Shopify AJAX)
    document.addEventListener('page:unload', function cleanup() {
      clearInterval(intervalId);
      document.removeEventListener('page:unload', cleanup);
    });
  }

  // Initialize all estimators on the page
  function init() {
    var api;
    try {
      api = createDelivery(window.KTDeliveryPromise);
    } catch (error) {
      // No promise document rendered: every estimator keeps its server-rendered
      // "Next working day", which is true whenever the page is served.
      return;
    }
    window.KTDelivery = api;
    var estimators = document.querySelectorAll('[data-delivery-estimator]');
    for (var i = 0; i < estimators.length; i++) {
      initEstimator(estimators[i], api);
    }
  }

  // Exposed before init so a consumer loaded earlier can build its own instance,
  // and so tests can drive the calendar directly.
  if (typeof window !== 'undefined') {
    window.KTDeliveryFactory = createDelivery;
  }
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { createDelivery: createDelivery, getUKTime: getUKTime };
  }

  if (typeof document === 'undefined') {
    // Loaded in a test/Node context — nothing to initialise.
  } else if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
